import { createApiClient } from "./api";
import { createBootstrapStore } from "./bootstrap";
import {
  createEventBus,
  GoCardlessEvent,
  GoCardlessInternalEvent,
} from "./bus";
import { Core } from "./core";
import { DEFAULT_LOCALE } from "./i18n";
import { attachSegmentAnalytics, createAnalyticsInstance } from "./segment";
import { COMPONENT_LOADERS } from "@src/registry";
import {
  ComponentConfigMap,
  GcComponent,
  GoCardlessConfig,
  GoCardlessInstance,
  Scheme,
} from "./types";
import { captureSentryError, initializeSentry } from "./helpers/sentry";

const INTERNAL_EVENT_TYPES = [
  "analytics:track",
  "api:requestStart",
  "api:requestEnd",
  "api:error",
] as const;

function setup(
  config: GoCardlessConfig,
  onInternalEvent?: (event: GoCardlessInternalEvent) => void
): GoCardlessInstance {
  const {
    environment,
    sessionToken,
    appearance,
    components = [],
    enableAnalytics = true,
    onEvent,
    onError,
    locale = DEFAULT_LOCALE,
  } = config;

  const bus = createEventBus();
  const api = createApiClient(environment, sessionToken, bus, locale);

  initializeSentry(config);

  const analytics = createAnalyticsInstance(enableAnalytics, environment);
  attachSegmentAnalytics(bus, analytics);

  if (onInternalEvent) {
    INTERNAL_EVENT_TYPES.forEach((type) =>
      bus.on(type, (body) =>
        onInternalEvent({ type, body } as GoCardlessInternalEvent)
      )
    );
  }

  const bootstrapStore = createBootstrapStore(api.register("sdk-bootstrap"));

  // Wires a component's declared publicEvents to onEvent, returning the
  // bus's unsubscribe functions so callers can undo it on unmount.
  const registerPublicEvents = (component: GcComponent): (() => void)[] => {
    if (!onEvent) return [];

    return (component.publicEvents ?? []).map((type) =>
      bus.on(type, (body) =>
        onEvent({ type, component: component.name, body } as GoCardlessEvent)
      )
    );
  };

  const install = (component: GcComponent, target?: string) => {
    const resolvedOnError = component.onError ?? onError ?? (() => {});

    const componentBootstrapData = bootstrapStore
      .request(component.bootstrapDataKeys ?? [])
      .catch((error) => {
        resolvedOnError(error);
        throw error;
      });

    const core = new Core(
      component.name,
      bus,
      api.register(component.name),
      appearance,
      resolvedOnError,
      componentBootstrapData,
      locale
    );
    component.mount(core, target);
  };

  // Register public events and mount components
  const staticUnsubscribes = components.flatMap((component) => {
    const unsubscribes = registerPublicEvents(component);
    install(component);
    return unsubscribes;
  });

  return {
    createComponent<Name extends keyof ComponentConfigMap>(
      name: Name,
      config?: ComponentConfigMap[Name]
    ) {
      const loadFactory = COMPONENT_LOADERS[name];

      if (!loadFactory) {
        captureSentryError(new Error(`Component ${name} is not registered`));
        throw new Error(`Component ${name} is not registered`);
      }

      const resolvedConfig = config ?? ({} as ComponentConfigMap[Name]);

      let unsubscribes: (() => void)[] = [];
      let component: GcComponent | undefined;
      let unmounted = false;

      return {
        async mount(target: string) {
          const factory = await loadFactory();

          if (unmounted) return;

          component = factory(resolvedConfig);
          unsubscribes = registerPublicEvents(component);
          install(component, target);
        },
        unmount() {
          unmounted = true;
          unsubscribes.forEach((unsubscribe) => unsubscribe());
          unsubscribes = [];
          component?.unmount();
          component = undefined;
        },
      };
    },
    unmountAll() {
      staticUnsubscribes.forEach((unsubscribe) => unsubscribe());
      components.forEach((component) => component.unmount());
    },
  };
}

function init(config: GoCardlessConfig): GoCardlessInstance {
  return setup(config);
}

// For demo/playground purposes, we allow the SDK to be initialised with
// a callback that receives internal events. This is not intended for
// production use, and is not part of the public API.
export function initWithInternalEvents(
  config: GoCardlessConfig,
  onInternalEvent: (event: GoCardlessInternalEvent) => void
): GoCardlessInstance {
  return setup(config, onInternalEvent);
}

export { init, Scheme };
